import React, { useState, useCallback } from "react";
import {
    ShieldCheck,
    ShieldAlert,
    Shield,
    Loader2,
    ChevronRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import ApiSecurityDetails from "./ApiSecurityDetails";

function StatusIcon({ state, passed, warning }) {
    if (state === "running")
        return <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />;
    if (state !== "done") return <Shield className="w-4 h-4 text-gray-300" />;
    if (!passed) return <ShieldAlert className="w-4 h-4 text-red-600" />;
    if (warning) return <ShieldAlert className="w-4 h-4 text-amber-500" />;
    return <ShieldCheck className="w-4 h-4 text-emerald-600" />;
}

function statusLabel(r, t) {
    if (!r) return "";
    if (r.state === "running") return t("appPreview.checking") || "Checking...";
    if (!r.passed) return t("appPreview.unprotected") || "No auth check";
    if (r.warning)
        return t("appPreview.protectedWithWarning") || "Protected · review";
    return t("appPreview.protected") || "Protected";
}

function statusColor(r) {
    if (!r || r.state !== "done") return "text-gray-500";
    if (!r.passed) return "text-red-600";
    if (r.warning) return "text-amber-600";
    return "text-emerald-600";
}

function detailColor(r) {
    if (!r.passed) return "text-red-600";
    if (r.warning) return "text-amber-600";
    return "text-gray-500";
}

export default function ApiSecurityRow({ func, result, onFixInChat }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const r = result;
    const isDone = r?.state === "done";

    const toggle = useCallback(() => {
        if (isDone) setExpanded((v) => !v);
    }, [isDone]);

    const onKeyDown = useCallback(
        (e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggle();
            }
        },
        [toggle]
    );

    return (
        <li>
            <div
                role={isDone ? "button" : undefined}
                tabIndex={isDone ? 0 : -1}
                onClick={toggle}
                onKeyDown={onKeyDown}
                className={`px-6 py-3 flex items-center gap-3 transition-colors ${
                    isDone ? "cursor-pointer hover:bg-gray-50" : "cursor-default"
                }`}
            >
                <StatusIcon
                    state={r?.state}
                    passed={r?.passed}
                    warning={r?.warning}
                />
                <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs text-gray-700 truncate">
                        {func.functionName}
                    </div>
                    {isDone && r.detail && (
                        <div
                            className={`text-[11px] mt-0.5 truncate ${detailColor(
                                r
                            )}`}
                        >
                            {r.detail}
                        </div>
                    )}
                </div>
                <span
                    className={`text-xs font-medium shrink-0 ${statusColor(r)}`}
                >
                    {statusLabel(r, t)}
                </span>
                <ChevronRight
                    className={`w-4 h-4 shrink-0 text-gray-400 transition-transform duration-200 ${
                        !isDone ? "opacity-0" : ""
                    } ${expanded ? "rotate-90" : ""}`}
                />
            </div>
            {expanded && (
                <ApiSecurityDetails
                    result={r}
                    func={func}
                    onFixInChat={onFixInChat}
                />
            )}
        </li>
    );
}
